import type { AgentMode, ConversationWrites, DeskDetail, DeskStage, WorkItemApprovalMode, Workspace } from '@deskworks/shared-types';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Confirm } from '@/components/confirm';
import { AGENT_MODE_INFO } from '@/components/desk/agent-mode-toggle';
import { DESK_STAGE_INFO } from '@/components/desk/stage-toggle';
import { Field } from '@/components/field';
import { LeadAgentPicker } from '@/components/lead-agent-picker';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api, errorMessage } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

export const NONE = '__none__';

const APPROVAL_MODES: { value: WorkItemApprovalMode; label: string; hint: string }[] = [
  { value: 'manual', label: t('A person approves'), hint: t('Work Items the Lead Agent proposes wait until someone approves them.') },
  { value: 'agent', label: t('An agent approves'), hint: t('Another agent of the desk checks each proposed Work Item against the desk’s goals and playbook.') },
  { value: 'auto', label: t('Start automatically'), hint: t('Proposed Work Items start right away.') },
];

const CHAT_WRITES: { value: ConversationWrites; label: string; hint: string }[] = [
  { value: 'never', label: t('Never — chats only read'), hint: t('Agents only read in chats; changes happen through Work Items.') },
  {
    value: 'off',
    label: t('When switched on in the chat'),
    hint: t('New chats only read. Switch on “Agent may write” in a chat, and the agent uses writing tools there; calls set to “ask” wait for your OK in the chat.'),
  },
  {
    value: 'on',
    label: t('Yes — new chats start writing'),
    hint: t('New chats start with “Agent may write” on; you can switch it off per chat. Calls set to “ask” still wait for your OK in the chat.'),
  },
];

/**
 * Create a Desk. Inside a workspace pass `workspaceId`; on account level pass `workspaces`
 * (a workspace select is shown first and the lead agent list follows the chosen workspace).
 */
export function CreateDeskDialog({
  workspaceId: fixedWorkspaceId,
  workspaces,
  defaultWorkspaceId,
  accountId,
  children,
}: {
  workspaceId?: string;
  workspaces?: Workspace[];
  defaultWorkspaceId?: string;
  /** Account whose desk list should be refreshed after creation. */
  accountId?: string;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [chosenWorkspaceId, setChosenWorkspaceId] = useState(defaultWorkspaceId ?? '');
  const workspaceId = fixedWorkspaceId ?? chosenWorkspaceId;
  const [name, setName] = useState('');
  const [assignment, setAssignment] = useState('');
  const [leadAgentId, setLeadAgentId] = useState('');
  const [playbook, setPlaybook] = useState('');

  const reset = () => {
    setName('');
    setAssignment('');
    setLeadAgentId('');
    setPlaybook('');
  };

  const onOpenChange = (o: boolean) => {
    if (o && !fixedWorkspaceId) {
      setChosenWorkspaceId(defaultWorkspaceId ?? (workspaces?.length === 1 ? workspaces[0].id : ''));
      setLeadAgentId('');
    }
    if (o) create.reset();
    setOpen(o);
  };

  const create = useApiMutation(
    () =>
      api.desks.create(workspaceId, {
        name: name.trim(),
        assignment: assignment.trim() || undefined,
        leadAgentId,
        playbook: playbook.trim() || undefined,
      }),
    {
      success: t('Desk created'),
      invalidate: [qk.desks(workspaceId), qk.overview(workspaceId), ...(accountId ? [qk.accountDesks(accountId)] : [])],
      onSuccess: (desk) => {
        setOpen(false);
        reset();
        void navigate({ to: '/w/$workspaceId/desks/$deskId', params: { workspaceId: desk.workspaceId || workspaceId, deskId: desk.id } });
      },
    },
  );

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim() && workspaceId && leadAgentId) create.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <form onSubmit={onSubmit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{t('New desk')}</DialogTitle>
            <DialogDescription>
              {t('A desk is an area of work you hand over to Deskworks — e.g. “Answer support tickets”. The Lead Agent decides what needs to be done and creates Work Items.')}
            </DialogDescription>
          </DialogHeader>
          {!fixedWorkspaceId && (
            <Field label={t('Workspace')} hint={t('Which part of your company does this desk work in? Agents and Connections come from this Workspace.')}>
              <Select
                value={chosenWorkspaceId}
                onValueChange={(v) => {
                  setChosenWorkspaceId(v);
                  setLeadAgentId('');
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('Choose a Workspace')} />
                </SelectTrigger>
                <SelectContent>
                  {(workspaces ?? []).map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field label={t('Name')}>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('e.g. Daily Ops Cases')} autoFocus={!!fixedWorkspaceId} required />
          </Field>
          <Field label={t('Assignment')} hint={t('What should this desk achieve? One or two sentences are enough.')} optional>
            <Textarea
              value={assignment}
              onChange={(e) => setAssignment(e.target.value)}
              rows={3}
              placeholder={t('e.g. Review new cases in the ticket system, analyze them and prepare a reply.')}
            />
          </Field>
          <div className="grid gap-1.5">
            <span className="text-sm font-medium">{t('Lead Agent')}</span>
            <LeadAgentPicker workspaceId={workspaceId} value={leadAgentId} onChange={setLeadAgentId} />
            <p className="text-xs text-muted-foreground">
              {t('Your contact person on this desk: it evaluates incoming events and hands out work. It needs a runtime (an AI model).')}
            </p>
          </div>
          <Field label={t('Playbook')} hint={t('How this desk works: binding rules, e.g. tone, limits, when an approval is needed.')} optional>
            <Textarea value={playbook} onChange={(e) => setPlaybook(e.target.value)} rows={6} className="font-mono text-[13px]" />
          </Field>
          {create.isError && (
            <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {errorMessage(create.error)}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button type="submit" loading={create.isPending} disabled={!name.trim() || !workspaceId || !leadAgentId}>
              {t('Create desk')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function EditDeskDialog({
  desk,
  children,
  open: controlledOpen,
  onOpenChange,
}: {
  desk: DeskDetail;
  children?: ReactNode; open?: boolean; onOpenChange?: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [innerOpen, setInnerOpen] = useState(false);
  const open = controlledOpen ?? innerOpen;
  const setOpen = onOpenChange ?? setInnerOpen;
  const [name, setName] = useState(desk.name);
  const [assignment, setAssignment] = useState(desk.assignment);
  const [parallel, setParallel] = useState(String(desk.maxParallelWorkItems));
  const [singular, setSingular] = useState(desk.workItemLabel.singular);
  const [plural, setPlural] = useState(desk.workItemLabel.plural);
  const [approval, setApproval] = useState<WorkItemApprovalMode>(desk.workItemApproval);
  const [approverId, setApproverId] = useState(desk.approverAgentId ?? '');
  const [agentMode, setAgentMode] = useState<AgentMode>(desk.agentMode);
  const [stage, setStage] = useState<DeskStage>(desk.stage);
  const [hold, setHold] = useState(String(desk.actionHoldSeconds));
  const [approvalWindow, setApprovalWindow] = useState(String(desk.actionApprovalWindowMinutes));
  const [chatWrites, setChatWrites] = useState<ConversationWrites>(desk.conversationWrites);
  // The Lead proposes the work, so another agent of the desk approves it.
  const approvers = desk.agents.filter((a) => a.role !== 'lead' && a.enabled);

  useEffect(() => {
    if (open) {
      setName(desk.name);
      setAssignment(desk.assignment);
      setParallel(String(desk.maxParallelWorkItems));
      setSingular(desk.workItemLabel.singular);
      setPlural(desk.workItemLabel.plural);
      setApproval(desk.workItemApproval);
      setApproverId(desk.approverAgentId ?? '');
      setAgentMode(desk.agentMode);
      setStage(desk.stage);
      setHold(String(desk.actionHoldSeconds));
      setApprovalWindow(String(desk.actionApprovalWindowMinutes));
      setChatWrites(desk.conversationWrites);
    }
  }, [open, desk.name, desk.assignment, desk.maxParallelWorkItems, desk.workItemApproval, desk.approverAgentId, desk.agentMode, desk.stage, desk.workItemLabel, desk.actionHoldSeconds, desk.actionApprovalWindowMinutes, desk.conversationWrites]);

  const parallelValue = Number(parallel);
  const parallelValid = Number.isInteger(parallelValue) && parallelValue >= 1 && parallelValue <= 50;
  const approverMissing = approval === 'agent' && !approverId;
  const holdValue = Number(hold);
  const holdValid = Number.isInteger(holdValue) && holdValue >= 0 && holdValue <= 600;
  const windowValue = Number(approvalWindow);
  const windowValid = Number.isInteger(windowValue) && windowValue >= 0 && windowValue <= 43_200;
  const save = useApiMutation(
    () =>
      api.desks.update(desk.id, {
        name: name.trim(),
        assignment: assignment.trim(),
        maxParallelWorkItems: parallelValue,
        workItemLabel: { singular: singular.trim() || 'Work Item', plural: plural.trim() || 'Work Items' },
        workItemApproval: approval,
        approverAgentId: approval === 'agent' ? approverId : desk.approverAgentId,
        agentMode,
        stage,
        actionHoldSeconds: holdValue,
        actionApprovalWindowMinutes: windowValue,
        conversationWrites: chatWrites,
      }),
    {
      success: t('Saved'),
      invalidate: [qk.desk(desk.id), qk.desks(desk.workspaceId)],
      onSuccess: () => setOpen(false),
    },
  );
  const canSave = !!name.trim() && parallelValid && !approverMissing && holdValid && windowValid;
  const remove = useApiMutation(() => api.desks.delete(desk.id), {
    success: t('Desk deleted'),
    invalidate: [qk.desks(desk.workspaceId)],
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: qk.desk(desk.id) });
      void navigate({ to: '/w/$workspaceId/desks', params: { workspaceId: desk.workspaceId } });
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent className="sm:max-w-xl">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSave) save.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>{t('Edit desk')}</DialogTitle>
          </DialogHeader>
          <Field label={t('Name')}>
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </Field>
          <Field label={t('Assignment')}>
            <Textarea value={assignment} onChange={(e) => setAssignment(e.target.value)} rows={4} />
          </Field>
          <Field label={t('What do you call work items here?')} hint={t('Singular and plural, e.g. Case / Cases or Ticket / Tickets. People and agents use the word; the API keeps saying Work Item.')}>
            <div className="grid grid-cols-2 gap-2">
              <Input value={singular} onChange={(e) => setSingular(e.target.value)} placeholder="Work Item" maxLength={40} aria-label={t('Singular')} />
              <Input value={plural} onChange={(e) => setPlural(e.target.value)} placeholder="Work Items" maxLength={40} aria-label={t('Plural')} />
            </div>
          </Field>
          <div className="grid gap-4 sm:grid-cols-[10rem_1fr]">
            <Field label={t('{items} at a time', { items: plural.trim() || 'Work Items' })} hint={t('The rest wait in line.')} error={parallelValid ? undefined : t('Between 1 and 50')}>
              <Input type="number" min={1} max={50} value={parallel} onChange={(e) => setParallel(e.target.value)} required />
            </Field>
            <Field label={t('Before new work starts')} hint={APPROVAL_MODES.find((m) => m.value === approval)?.hint}>
              <Select value={approval} onValueChange={(v) => setApproval(v as WorkItemApprovalMode)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {APPROVAL_MODES.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          {approval === 'agent' && (
            <Field
              label={t('Approving agent')}
              hint={approvers.length ? t('Checks every Work Item the Lead Agent proposes. If it can’t decide, a person does.') : undefined}
              error={approvers.length ? undefined : t('Add another agent to this desk first — the Lead Agent can’t approve its own proposals.')}
            >
              <Select value={approverId || NONE} onValueChange={(v) => setApproverId(v === NONE ? '' : v)} disabled={!approvers.length}>
                <SelectTrigger>
                  <SelectValue placeholder={t('Choose an agent')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t('Choose an agent')}</SelectItem>
                  {approvers.map((a) => (
                    <SelectItem key={a.agentId} value={a.agentId}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field label={t('Stage')} hint={DESK_STAGE_INFO[stage].hint}>
            <Select value={stage} onValueChange={(v) => setStage(v as DeskStage)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="learning">{t('Learning — it learns the work, asks and writes playbooks')}</SelectItem>
                <SelectItem value="copilot">{t('Co-Pilot — it does the work, you approve each result')}</SelectItem>
                <SelectItem value="autopilot">{t('Autopilot — it works on its own, asks only what matters')}</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={t('God mode')} hint={AGENT_MODE_INFO[agentMode].hint}>
            <Select value={agentMode} onValueChange={(v) => setAgentMode(v as AgentMode)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ask">{t('Off — agents stop as the stage says')}</SelectItem>
                <SelectItem value="god">{t('God mode — they never stop and keep trying')}</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {/* docs/features/actions-and-permissions.md */}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label={t('Wait for my approval (seconds)')}
              hint={t('How long a tool call that asks you waits in the run. Decide within it and the agent just continues; after it, the work parks until you decide.')}
              error={holdValid ? undefined : t('Between 0 and 600')}
            >
              <Input type="number" min={0} max={600} value={hold} onChange={(e) => setHold(e.target.value)} />
            </Field>
            <Field
              label={t('Run approved calls directly within (minutes)')}
              hint={t('Approved later than this, the agent checks the call again before it runs. 0: it always checks first.')}
              error={windowValid ? undefined : t('Between 0 and 43200')}
            >
              <Input type="number" min={0} max={43_200} value={approvalWindow} onChange={(e) => setApprovalWindow(e.target.value)} />
            </Field>
          </div>
          {/* docs/features/conversations.md#writing-in-a-chat */}
          <Field label={t('Chats may write')} hint={CHAT_WRITES.find((m) => m.value === chatWrites)?.hint}>
            <Select value={chatWrites} onValueChange={(v) => setChatWrites(v as ConversationWrites)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CHAT_WRITES.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <p className="text-xs text-muted-foreground">
            {t('Work you create yourself or ask for in a conversation always counts as approved — and so do the sub-tasks and reviews of approved work.')}
          </p>
          <DialogFooter className="sm:justify-between">
            <Confirm
              title={t('Delete desk “{name}”?', { name: desk.name })}
              description={t('The desk is deleted with its Work Items, conversations, triggers and history; running workers are stopped. The agents stay in the workspace. This cannot be undone.')}
              confirmLabel={t('Delete desk')}
              destructive
              onConfirm={() => remove.mutateAsync()}
            >
              <Button type="button" variant="ghost" className="text-destructive hover:text-destructive">
                <Trash2 /> {t('Delete')}
              </Button>
            </Confirm>
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                {t('Cancel')}
              </Button>
              <Button type="submit" loading={save.isPending} disabled={!canSave}>
                {t('Save')}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
